import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../components/UserContext.tsx';
import { useCrypto } from '../../components/CryptoContext.tsx';
import { accessApi, type AccessOverview } from '../../access/api.ts';
import { createAccessCeremony, type AccessCeremony } from '../../access/ceremony.ts';
import AccessLimitations from './AccessLimitations.tsx';
import './AccessCenter.scss';

function remainingLabel(expiresAt: string) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${minutes}m remaining`;
}

function lookupName(overview: AccessOverview | null, userId: string) {
  const user = overview?.users.find((row) => row.id === userId);
  return user?.fullName || user?.shortName || userId.slice(-6);
}

export default function AccessCenter({ ceremony }: { ceremony: AccessCeremony }) {
  const { activeUser } = useUser();
  const { status, exportAccessRootForCustody } = useCrypto();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<AccessOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [targetEmail, setTargetEmail] = useState('');
  const [directorateEmail, setDirectorateEmail] = useState('');
  const [depotEmail, setDepotEmail] = useState('');
  const [providerEmail, setProviderEmail] = useState('');
  const [orderSetId, setOrderSetId] = useState('');
  const [sessionOrderId, setSessionOrderId] = useState('');
  const [releaseSessionId, setReleaseSessionId] = useState('');
  const [attestSessionId, setAttestSessionId] = useState('');
  const [includeFamily, setIncludeFamily] = useState(false);

  const reload = useCallback(async () => {
    const next = await accessApi.getOverview();
    setOverview(next);
    setOrderSetId((current) => current || next.custodySets[0]?.id || '');
    setSessionOrderId((current) => current || next.orders[0]?.id || '');
    setReleaseSessionId((current) => current || next.sessions[0]?.id || '');
    setAttestSessionId((current) => current || next.sessions[0]?.id || '');
  }, []);

  useEffect(() => {
    reload().catch((cause) => {
      setError(cause instanceof Error ? cause.message : 'Could not load the mock-order drill.');
    });
  }, [reload]);

  const pendingAssignments = useMemo(
    () => overview?.assignments || [],
    [overview]
  );

  async function run(label: string, work: () => Promise<void>) {
    setBusy(label);
    setError(null);
    try {
      await work();
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That court-access step failed.');
    } finally {
      setBusy(null);
    }
  }

  const registerKey = () => run('key', async () => {
    await ceremony.ensureCustodianKey();
  });

  const createSet = () => run('custody', async () => {
    const target = await accessApi.lookupUser(targetEmail);
    const directorate = await accessApi.lookupUser(directorateEmail);
    const depot = await accessApi.lookupUser(depotEmail);
    const provider = await accessApi.lookupUser(providerEmail);
    const keys = await Promise.all([
      accessApi.getLabKey(directorate.id),
      accessApi.getLabKey(depot.id),
      accessApi.getLabKey(provider.id)
    ]);
    await accessApi.createCustodySet({
      targetUserId: target.id,
      custodians: [
        { role: 'directorate', holderUserId: directorate.id, custodyPublicKeyJwk: keys[0].publicKeyJwk },
        { role: 'depot', holderUserId: depot.id, custodyPublicKeyJwk: keys[1].publicKeyJwk },
        { role: 'provider', holderUserId: provider.id, custodyPublicKeyJwk: keys[2].publicKeyJwk }
      ]
    });
  });

  const deposit = () => run('deposit', async () => {
    if (status !== 'unlocked') {
      throw new Error('Unlock encryption before depositing escrow shares.');
    }
    if (!activeUser?.id) throw new Error('Log in again.');
    const root = await exportAccessRootForCustody();
    try {
      await ceremony.depositShares(root, String(activeUser.id));
    } finally {
      root.fill(0);
    }
  });

  const activate = (id: string) => run('activate', async () => {
    const set = overview?.custodySets.find((row) => row.id === id);
    await accessApi.activateCustodySet(id, { periodId: set?.periodId });
  });

  const createOrder = () => run('order', async () => {
    const set = overview?.custodySets.find((row) => row.id === orderSetId);
    if (!set?.periodId) throw new Error('Activate the custody set first.');
    const start = new Date();
    const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    await accessApi.createOrder({
      custodySetId: set.id,
      periodId: set.periodId,
      periodStart: start.toISOString(),
      periodEnd: end.toISOString(),
      expiresAt: end.toISOString(),
      corpusScope: includeFamily ? ['adult-private', 'family'] : ['adult-private']
    });
  });

  const openSession = () => run('session', async () => {
    const session = await ceremony.openInvestigatorSession(sessionOrderId);
    navigate(`/access/session/${session.id}`);
  });

  const release = () => run('release', async () => {
    await ceremony.releaseForSession(releaseSessionId);
  });

  const attest = (matched: boolean) => run('attest', async () => {
    await accessApi.attest(attestSessionId, matched);
  });

  if (!overview && !error) {
    return (
      <div className="main-content access-center">
        <Container className="my-4">
          <Spinner animation="border" aria-hidden="true" />
          <p>Loading the mock-order drill…</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content access-center">
      <Container className="my-4">
        <div className="access-header mb-3">
          <div>
            <h1 className="display-6">Court-access drill</h1>
            <p className="text-body-secondary mb-0">
              Split-key escrow for a signed mock order. Not ordinary end-to-end encryption.
            </p>
          </div>
          <Button variant="outline-secondary" onClick={() => reload()} disabled={!!busy}>Reload</Button>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {overview && <Alert variant="secondary">{overview.limitation}</Alert>}

        <Tabs defaultActiveKey="setup" className="mb-3">
          <Tab eventKey="setup" title="Setup">
            <Row className="g-3">
              <Col lg={6}>
                <Card>
                  <Card.Body>
                    <Card.Title as="h2" className="h5">Lab custodian key</Card.Title>
                    <p>Each simulated custodian registers a key in this browser, then logs in as that account.</p>
                    <Button onClick={registerKey} disabled={!!busy}>
                      {overview?.labKeyRegistered ? 'Lab key registered' : 'Create lab custodian key'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <Card.Body>
                    <Card.Title as="h2" className="h5">Create a 2-of-3 custody set</Card.Title>
                    <Form.Group className="mb-2">
                      <Form.Label>Target email</Form.Label>
                      <Form.Control value={targetEmail} onChange={(event) => setTargetEmail(event.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Directorate email</Form.Label>
                      <Form.Control value={directorateEmail} onChange={(event) => setDirectorateEmail(event.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Depot email</Form.Label>
                      <Form.Control value={depotEmail} onChange={(event) => setDepotEmail(event.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Provider email</Form.Label>
                      <Form.Control value={providerEmail} onChange={(event) => setProviderEmail(event.target.value)} />
                    </Form.Group>
                    <Button onClick={createSet} disabled={!!busy}>Create custody set</Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <Card.Body>
                    <Card.Title as="h2" className="h5">Target deposit</Card.Title>
                    <p>If this login is the named adult, deposit escrow shares. That is setup, not an order, and it does not notify anyone.</p>
                    <Button onClick={deposit} disabled={!!busy || status !== 'unlocked'}>
                      Deposit this week’s access shares
                    </Button>
                    {status !== 'unlocked' && (
                      <p className="text-body-secondary mt-2 mb-0">Unlock encryption in Settings first.</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <Card.Body>
                    <Card.Title as="h2" className="h5">Activate</Card.Title>
                    {(overview?.custodySets || []).map((set) => (
                      <div key={set.id} className="mb-2">
                        <div>
                          {set.state} · target {lookupName(overview, set.targetUserId)} · {set.periodId || 'no period yet'}
                        </div>
                        {set.state === 'draft' && (
                          <Button size="sm" className="mt-1" onClick={() => activate(set.id)} disabled={!!busy}>
                            Activate
                          </Button>
                        )}
                      </div>
                    ))}
                    {!overview?.custodySets.length && <p className="text-body-secondary mb-0">No custody sets visible to this login.</p>}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="registry" title="Registry">
            <Card>
              <Card.Body>
                <Card.Title as="h2" className="h5">Sign a mock order</Card.Title>
                <p>Four weeks is the default ceiling. This lab window is one week. Encrypted chat is always in the order. Family only if you tick it. School chat cannot be added.</p>
                <Form.Select className="mb-2" value={orderSetId} onChange={(event) => setOrderSetId(event.target.value)}>
                  {(overview?.custodySets || []).map((set) => (
                    <option key={set.id} value={set.id}>{set.id} ({set.state})</option>
                  ))}
                </Form.Select>
                <Form.Check
                  className="mb-2"
                  type="checkbox"
                  id="access-include-family"
                  label="Include family threads in this mock order"
                  checked={includeFamily}
                  onChange={(event) => setIncludeFamily(event.target.checked)}
                />
                <Button onClick={createOrder} disabled={!!busy || !orderSetId}>Create signed mock order</Button>
                <ul className="mt-3">
                  {(overview?.orders || []).map((order) => (
                    <li key={order.id}>
                      {order.caseReference || order.id} · {(order.corpusScope || []).join(', ')} · {order.periodStart} → {order.periodEnd}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="custodian" title="Custodian">
            <Card>
              <Card.Body>
                <Card.Title as="h2" className="h5">Re-encrypt a share to the session</Card.Title>
                <p>The signature is checked before the share is re-wrapped. One release cannot open the session.</p>
                <p>This login’s roles: {pendingAssignments.map((row) => row.role).join(', ') || 'none'}</p>
                <Form.Select className="mb-2" value={releaseSessionId} onChange={(event) => setReleaseSessionId(event.target.value)}>
                  {(overview?.sessions || []).map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.caseReference || session.id} · {session.state} · {session.releaseCount}/2
                    </option>
                  ))}
                </Form.Select>
                <Button onClick={release} disabled={!!busy || !releaseSessionId}>Release this custodian share</Button>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="investigator" title="Investigator">
            <Card>
              <Card.Body>
                <Card.Title as="h2" className="h5">Open a viewing session</Card.Title>
                <p>Plaintext stays on this workstation. There is no download control.</p>
                <Form.Select className="mb-2" value={sessionOrderId} onChange={(event) => setSessionOrderId(event.target.value)}>
                  {(overview?.orders || []).map((order) => (
                    <option key={order.id} value={order.id}>{order.caseReference || order.id}</option>
                  ))}
                </Form.Select>
                <Button onClick={openSession} disabled={!!busy || !sessionOrderId}>Open investigator session</Button>
                <ul className="mt-3">
                  {(overview?.sessions || []).map((session) => (
                    <li key={session.id}>
                      {session.caseReference || session.id} · {session.state} · {remainingLabel(session.expiresAt)}
                      {session.state === 'quorum-met' && (
                        <> · <Link to={`/access/session/${session.id}`}>Open viewing shell</Link></>
                      )}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="witness" title="Witness">
            <Card>
              <Card.Body>
                <Card.Title as="h2" className="h5">Åpen Dør log</Card.Title>
                <p>Attest or flag. Witnesses do not submit shares and cannot veto a valid order.</p>
                <Form.Select className="mb-2" value={attestSessionId} onChange={(event) => setAttestSessionId(event.target.value)}>
                  {(overview?.sessions || []).map((session) => (
                    <option key={session.id} value={session.id}>{session.caseReference || session.id}</option>
                  ))}
                </Form.Select>
                <Button className="me-2" onClick={() => attest(true)} disabled={!!busy || !attestSessionId}>Attest: scope matched</Button>
                <Button variant="outline-danger" onClick={() => attest(false)} disabled={!!busy || !attestSessionId}>Flag: scope mismatch</Button>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
        <AccessLimitations />
      </Container>
    </div>
  );
}
